import { useState } from 'react';
import { useMySession, useOpenRegister, useCloseRegister, useCheckout, useProductSearch, useRecentSales, useSuspendedCarts, useSuspendCart, useResumeCart, useVoidSale, useClosedRegisterSessions, useApproveVariance } from '../hooks/usePosApi';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { formatINR } from '@/shared/utils/currency';
import { ShoppingCart, Plus, Minus, Trash2, Pause, Play, X, Ban } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUser } from '@/shared/hooks/useAuth';

const ADMIN_ROLES = ['superadmin', 'admin', 'department_admin', 'ceo', 'owner'];

function RegisterVariancePanel() {
  const { data: sessions } = useClosedRegisterSessions();
  const approveMut = useApproveVariance();
  const [explaining, setExplaining] = useState<string | null>(null);
  const [explanation, setExplanation] = useState('');

  if (!sessions || sessions.length === 0) return null;

  const approve = async (sessionId: string) => {
    try {
      await approveMut.mutateAsync({ sessionId, explanation });
      toast.success('Variance approved');
      setExplaining(null); setExplanation('');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Approval failed'); }
  };

  return (
    <div className="border-t bg-warning/10 dark:bg-warning/10/20 p-4">
      <p className="text-xs font-semibold text-warning-text dark:text-warning-text mb-2">CASH VARIANCE — NEEDS APPROVAL ({sessions.length})</p>
      <div className="space-y-2">
        {sessions.map((s) => (
          <div key={s._id} className="bg-card border rounded-lg p-2.5 text-xs">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">{s.cashierId?.display_name || 'Cashier'}</span>
                <span className="ml-2 text-muted-foreground">{s.closedAt ? new Date(s.closedAt).toLocaleString('en-IN') : ''}</span>
              </div>
              <span className={`font-semibold ${(s.variance || 0) < 0 ? 'text-destructive' : 'text-success-text'}`}>
                {(s.variance || 0) > 0 ? '+' : ''}{formatINR(s.variance)}
              </span>
            </div>
            {explaining === s._id ? (
              <div className="mt-2 flex gap-2">
                <Input name="explanation" value={explanation} onChange={(e) => setExplanation(e.target.value)} placeholder="Explanation (optional)" className="text-xs h-8" autoFocus />
                <Button size="sm" onClick={() => approve(s._id)} disabled={approveMut.isPending}>{approveMut.isPending ? 'Saving...' : 'Confirm'}</Button>
                <Button size="sm" variant="secondary" onClick={() => { setExplaining(null); setExplanation(''); }}>Cancel</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => { setExplaining(s._id); setExplanation(''); }}>Approve variance</Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface CartLine { productId: string; variantId?: string; productName: string; sku: string; quantity: number; unitPrice: number; discount: number; discountType: 'fixed' | 'percent'; cgstRate: number; sgstRate: number; igstRate: number; taxAmount: number; lineTotal: number; }

export function PosPage() {
  const user = useUser();
  const isAdmin = ADMIN_ROLES.includes(user?.role ?? '');
  const { data: session, isLoading } = useMySession();
  const openMut = useOpenRegister();
  const closeMut = useCloseRegister();
  const checkoutMut = useCheckout();
  const suspendMut = useSuspendCart();
  const resumeMut = useResumeCart();
  const voidMut = useVoidSale();
  const { data: recentSales } = useRecentSales();
  const { data: suspendedCarts } = useSuspendedCarts();

  const [cashInHand, setCashInHand] = useState('0');
  const [countedCash, setCountedCash] = useState('');
  const [showClose, setShowClose] = useState(false);
  const [showVoid, setShowVoid] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');

  // Cart state
  const [cart, setCart] = useState<CartLine[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: searchResults } = useProductSearch(searchQuery);
  const [customerName, setCustomerName] = useState('Walk-in');
  const [showCheckout, setShowCheckout] = useState(false);
  const [tenderAmount, setTenderAmount] = useState('');
  const [tenderMethod, setTenderMethod] = useState('cash');

  // Compute cart totals
  const subTotal = cart.reduce((s, l) => s + (l.quantity * l.unitPrice) - l.discount, 0);
  const taxTotal = cart.reduce((s, l) => s + l.taxAmount, 0);
  const grandTotal = Math.round((subTotal + taxTotal) * 100) / 100;

  const addToCart = (product: any) => {
    const existing = cart.find(l => l.productId === product._id);
    if (existing) { setCart(cart.map(l => l.productId === product._id ? recalcLine({ ...l, quantity: l.quantity + 1 }) : l)); }
    else {
      const tax = product.taxProfileId;
      const newLine: CartLine = { productId: product._id, productName: product.name, sku: product.sku || '', quantity: 1, unitPrice: product.sellingPrice || 0, discount: 0, discountType: 'fixed', cgstRate: tax?.cgstRate || 0, sgstRate: tax?.sgstRate || 0, igstRate: tax?.igstRate || 0, taxAmount: 0, lineTotal: 0 };
      setCart([...cart, recalcLine(newLine)]);
    }
    setSearchQuery('');
  };

  const recalcLine = (line: CartLine): CartLine => {
    const net = (line.quantity * line.unitPrice) - line.discount;
    // CGST+SGST (local sale) and IGST (interstate sale) are alternatives,
    // never cumulative. POS defaults to the local rate when it is configured.
    const applicableTaxRate = (line.cgstRate + line.sgstRate) || line.igstRate;
    const tax = net * (applicableTaxRate / 100);
    return { ...line, taxAmount: Math.round(tax * 100) / 100, lineTotal: Math.round((net + tax) * 100) / 100 };
  };

  const updateQty = (idx: number, delta: number) => { setCart(cart.map((l, i) => i === idx ? recalcLine({ ...l, quantity: Math.max(1, l.quantity + delta) }) : l)); };
  const removeLine = (idx: number) => { setCart(cart.filter((_, i) => i !== idx)); };

  const handleCheckout = async () => {
    const amt = Number(tenderAmount) || 0;
    if (amt < grandTotal) { toast.error(`Need at least ₹${grandTotal}`); return; }
    try {
      const result = await checkoutMut.mutateAsync({ lines: cart, tenders: [{ method: tenderMethod, amount: amt }], customerName, roundOff: 0 });
      toast.success(`Sale ${result.sale.saleNumber} complete! Change: ₹${result.changeGiven}`);
      setCart([]); setShowCheckout(false); setCustomerName('Walk-in'); setTenderAmount('');
    } catch (err: any) { toast.error(err.response?.data?.error || 'Checkout failed'); }
  };

  const handleSuspend = async () => {
    if (!cart.length) return;
    await suspendMut.mutateAsync({ lines: cart, customerName });
    toast.success('Cart suspended'); setCart([]);
  };

  const handleResume = async (cartId: string) => {
    const data = await resumeMut.mutateAsync(cartId);
    setCart(data.lines || []); setCustomerName(data.customerName || 'Walk-in');
    toast.success('Cart resumed');
  };

  // Not logged in / loading
  if (isLoading) return <div className="p-6 text-center">Loading POS...</div>;

  // No active session — show open register
  if (!session) {
    return (
      <div className="h-full flex flex-col overflow-auto bg-muted/20">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-card border rounded-2xl p-8 w-96 text-center space-y-4 shadow-lg">
            <ShoppingCart className="h-12 w-12 mx-auto text-primary" />
            <h2 className="text-xl font-semibold">Open Cash Register</h2>
            <p className="text-sm text-muted-foreground">Enter your opening cash in hand to start.</p>
            <Input name="cash" type="number" value={cashInHand} onChange={e => setCashInHand(e.target.value)} placeholder="₹ Opening Cash" className="text-center text-lg" />
            <Button className="w-full" onClick={() => openMut.mutateAsync({ cashInHand: Number(cashInHand) })} disabled={openMut.isPending}>{openMut.isPending ? 'Opening...' : 'Open Register'}</Button>
          </div>
        </div>
        {isAdmin && <RegisterVariancePanel />}
      </div>
    );
  }

  // Active session — POS interface
  return (
    <div className="h-full flex overflow-hidden">
      {/* Left: Product Search + Cart */}
      <div className="flex-1 flex flex-col border-r">
        {/* Search */}
        <div className="p-3 border-b bg-card">
          <Input name="search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search products by name, SKU or scan barcode..." className="text-sm" autoFocus />
          {searchResults && searchResults.length > 0 && searchQuery && (
            <div className="absolute z-50 mt-1 bg-card dark:bg-gray-800 border rounded-lg shadow-lg max-h-60 overflow-y-auto w-[calc(100%-3rem)]">
              {searchResults.map((p: any) => (
                <button key={p._id} onClick={() => addToCart(p)} className="w-full px-4 py-2 text-left hover:bg-muted/50 flex justify-between text-sm border-b last:border-0">
                  <span>{p.name} <span className="text-xs text-muted-foreground">{p.sku}</span></span>
                  <span className="font-medium">{formatINR(p.sellingPrice)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Cart Lines */}
        <div className="flex-1 overflow-auto p-3 space-y-2">
          {cart.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground"><p>Scan or search to add products</p></div>
          ) : cart.map((line, idx) => (
            <div key={idx} className="bg-card border rounded-lg p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{line.productName}</p><p className="text-xs text-muted-foreground">{formatINR(line.unitPrice)} × {line.quantity}</p></div>
              <div className="flex items-center gap-1">
                <button onClick={() => updateQty(idx, -1)} className="p-1 rounded hover:bg-muted"><Minus className="h-4 w-4" /></button>
                <span className="w-8 text-center font-medium">{line.quantity}</span>
                <button onClick={() => updateQty(idx, 1)} className="p-1 rounded hover:bg-muted"><Plus className="h-4 w-4" /></button>
              </div>
              <span className="font-semibold text-sm w-20 text-right">{formatINR(line.lineTotal)}</span>
              <button onClick={() => removeLine(idx)} className="p-1 text-destructive hover:bg-destructive/10 rounded"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>

        {/* Cart Actions */}
        {cart.length > 0 && (
          <div className="p-3 border-t bg-card flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSuspend}><Pause className="h-4 w-4 mr-1" /> Suspend</Button>
            <Button variant="outline" size="sm" onClick={() => setCart([])}><X className="h-4 w-4 mr-1" /> Clear</Button>
          </div>
        )}
      </div>

      {/* Right: Totals + Checkout + Session Info */}
      <div className="w-80 flex flex-col bg-card">
        <div className="p-4 border-b">
          <Input name="customer" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Customer name" className="text-sm mb-2" />
          <div className="mb-3 rounded-lg border border-info/30 bg-info/10 p-2.5 text-xs leading-5 text-info-text dark:border-info/30 dark:bg-info/10/30 dark:text-info-text">
            <strong>Total calculation:</strong> (Sell price × Qty − Discount) + Tax. Completing the sale reduces stock and records the payment tender.
          </div>
          <div className="space-y-1.5 text-body-sm">
            <div className="flex items-baseline justify-between gap-3"><span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Items after discount</span><span className="font-mono tabular-nums text-right text-on-surface">{formatINR(subTotal)}</span></div>
            <div className="flex items-baseline justify-between gap-3 text-on-surface-variant"><span className="text-label-sm uppercase tracking-wider">GST / Tax added</span><span className="font-mono tabular-nums text-right">{formatINR(taxTotal)}</span></div>
            <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-border pt-2"><span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Total</span><span className="font-mono tabular-nums text-right text-data-lg font-semibold text-on-surface">{formatINR(grandTotal)}</span></div>
          </div>
        </div>

        <div className="p-4">
          <Button className="w-full h-12 text-lg" onClick={() => setShowCheckout(true)} disabled={cart.length === 0}>Pay {formatINR(grandTotal)}</Button>
        </div>

        {/* Suspended Carts */}
        {suspendedCarts && suspendedCarts.length > 0 && (
          <div className="p-4 border-t"><p className="text-xs font-semibold text-muted-foreground mb-2">SUSPENDED ({suspendedCarts.length})</p>
            {suspendedCarts.slice(0, 3).map(c => (
              <button key={c._id} onClick={() => handleResume(c._id)} className="w-full text-left p-2 rounded border mb-1 hover:bg-muted/50 text-xs flex justify-between">
                <span>{c.customerName || 'Cart'} ({c.lines?.length} items)</span><Play className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}

        {/* Recent Sales with Void option */}
        {recentSales && recentSales.length > 0 && (
          <div className="p-4 border-t"><p className="text-xs font-semibold text-muted-foreground mb-2">RECENT SALES</p>
            {recentSales.slice(0, 5).map((s: any) => (
              <div key={s._id} className="flex items-center justify-between p-2 rounded border mb-1 text-xs">
                <div>
                  <span className="font-medium">{s.saleNumber}</span>
                  <span className="ml-2 text-muted-foreground">{formatINR(s.netTotal)}</span>
                  {s.status === 'voided' && <span className="ml-2 px-1.5 py-0.5 rounded bg-destructive/10 text-destructive text-[10px] font-medium">VOIDED</span>}
                </div>
                {s.status === 'completed' && (
                  <button onClick={() => { setShowVoid(s._id); setVoidReason(''); }} className="p-1 text-destructive hover:bg-destructive/10 rounded" title="Void sale"><Ban className="h-3.5 w-3.5" /></button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Session Controls */}
        <div className="mt-auto p-4 border-t text-xs text-muted-foreground">
          <p>Session opened: {new Date(session.openedAt).toLocaleTimeString('en-IN')}</p>
          <p>Sales this session: {recentSales?.length || 0}</p>
          <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => setShowClose(true)}>Close Register</Button>
        </div>

        {isAdmin && <RegisterVariancePanel />}
      </div>

      {/* Checkout Modal */}
      <Modal open={showCheckout} onClose={() => setShowCheckout(false)} title={`Checkout — ₹${grandTotal}`} size="sm" footer={
        <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowCheckout(false)}>Cancel</Button><Button onClick={handleCheckout} disabled={checkoutMut.isPending}>{checkoutMut.isPending ? 'Processing...' : 'Complete Sale'}</Button></div>
      }>
        <div className="py-4 space-y-3">
          <div className="flex gap-2">
            {['cash', 'card', 'upi'].map(m => (<button key={m} onClick={() => setTenderMethod(m)} className={`px-4 py-2 rounded-lg border text-sm font-medium capitalize ${tenderMethod === m ? 'bg-primary text-primary-foreground border-primary' : 'bg-background'}`}>{m}</button>))}
          </div>
          <Input name="amount" type="number" value={tenderAmount} onChange={e => setTenderAmount(e.target.value)} placeholder={`Amount (min ₹${grandTotal})`} className="text-lg text-center" autoFocus />
          {Number(tenderAmount) > grandTotal && <p className="text-sm text-success-text text-center">Change: {formatINR(Number(tenderAmount) - grandTotal)}</p>}
        </div>
      </Modal>

      {/* Close Register Modal */}
      <Modal open={showClose} onClose={() => setShowClose(false)} title="Close Register" size="sm" footer={
        <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowClose(false)}>Cancel</Button><Button onClick={async () => { await closeMut.mutateAsync({ countedCash: Number(countedCash) }); setShowClose(false); toast.success('Register closed'); }} disabled={closeMut.isPending}>Close & Settle</Button></div>
      }>
        <div className="py-4 space-y-3">
          <p className="text-sm text-muted-foreground">Count the cash in the drawer and enter the amount below.</p>
          <Input name="counted" type="number" value={countedCash} onChange={e => setCountedCash(e.target.value)} placeholder="Counted cash amount" className="text-lg text-center" />
        </div>
      </Modal>

      {/* Void Sale Modal */}
      <Modal open={!!showVoid} onClose={() => setShowVoid(null)} title="Void Sale" size="sm" footer={
        <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowVoid(null)}>Cancel</Button><Button variant="outline" onClick={async () => { if (!voidReason.trim()) { toast.error('Enter a reason'); return; } try { await voidMut.mutateAsync({ saleId: showVoid!, reason: voidReason }); toast.success('Sale voided — stock restored'); setShowVoid(null); } catch (err: any) { toast.error(err?.response?.data?.error || 'Void failed'); } }} disabled={voidMut.isPending}>{voidMut.isPending ? 'Voiding...' : 'Void Sale'}</Button></div>
      }>
        <div className="py-4 space-y-3">
          <p className="text-sm text-muted-foreground">This will reverse the sale and restore stock. This cannot be undone.</p>
          <Input name="void-reason" value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="Reason for void (required)" autoFocus />
        </div>
      </Modal>
    </div>
  );
}
